/**
 * English — release-channel
 *
 * All views migrated to vue-i18n. The remaining inline strings are
 * either (a) backend-shaped enums surfaced verbatim (channel names,
 * quality grades) or (b) cross-lingual DevOps shorthand (30d / 1y
 * window pills, CSV column headers).
 */
export default {
  app: {
    name: 'Release Channel',
  },
  nav: {
    dashboard: 'Dashboard',
    software: 'Software',
    channels: 'Channels',
    trains: 'Trains',
  },
  navGroups: {
    browse: 'Browse',
    manage: 'Manage',
  },
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    adjustRole: 'Adjust role',
    signInUnavailable: 'Sign-in is currently unavailable. Please contact the administrator.',
  },
  user: {
    profile: 'Profile',
  },
  sidebar: {
    shortcuts: 'Shortcuts',
    settings: 'Settings',
    settingsNotImpl: 'Settings page is not implemented yet.',
    collapse: 'Collapse',
    expand: 'Expand sidebar',
    collapseLong: 'Collapse sidebar (icons only)',
    skipLink: 'Skip to main content',
    accountMenu: 'Account menu',
    utilityMenu: 'More options',
    more: 'More',
    language: 'Language',
  },
  lang: {
    title: 'Language',
    'zh-CN': '简体中文',
    'en-US': 'English',
  },
  common: {
    loading: 'Loading…',
    retry: 'Retry',
    retrying: 'Retrying…',
  },
  a11y: {
    primaryNav: 'Primary',
    sectionsNav: 'Sections',
    pageContent: 'Page content',
    notifications: 'Notifications',
    dismissNotification: 'Dismiss notification',
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    escToClose: 'Press {esc} to close.',
    chordSeparator: 'then',
    close: 'Close',
    groups: {
      navigate: 'Navigate',
      onPage: 'On the page',
    },
    items: {
      goDashboard: 'Go to Dashboard',
      goSoftware: 'Go to Software',
      goChannels: 'Go to Channels',
      focusFilter: 'Focus the filter input',
      openHelp: 'Open this shortcut help',
      closeDialog: 'Close dialogs / cancel',
    },
  },
  cycleGantt: {
    loading: 'Loading cycle calendar…',
    emptyDays: 'No promotions in the last {days} days.',
    srCaption: 'Channel residency for the last {days} days, one row per channel span across {count} versions.',
    srColVersion: 'Version',
    srColChannel: 'Channel',
    srColStart: 'Start',
    srColEnd: 'End',
    srColStatus: 'Status',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Release channel overview across all software products.',
    refresh: 'Refresh',
    refreshTitleIdle: 'Re-fetch the dashboard summary.',
    updatedAgoTitleIdle: 'Click Refresh to re-fetch.',
    updatedAgo: 'Updated {ago}',
    stats: {
      software: 'Software',
      releases: 'Releases',
      releasesByChannel: 'Releases by channel',
      channelTooltipOne: '{count} release on {channel}',
      channelTooltipOther: '{count} releases on {channel}',
    },
    pipeline: {
      title: 'Release pipeline',
      pickSoftware: 'Pick software…',
      selectAria: 'Select software for the release pipeline view',
      empty: 'No software registered yet — create one to see its release cycles.',
      windowLabel: 'Window:',
      windowGroup: 'Cycle gantt time window',
    },
    promotions: {
      title: 'Recent promotions',
      empty: 'No promotions in the recent window. Channel promotions appear here as releases advance through devel → beta → stable.',
      by: 'by {user}',
    },
    releases: {
      title: 'Recent registrations',
      empty: 'No releases registered yet. Newly published versions land here as soon as the publisher confirms upload.',
    },
  },
  software: {
    title: 'Software',
    subtitle: 'Products and per-channel current versions, mirrored from Passgate. Adding new software happens in Passgate; click Refresh to pick it up.',
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    refreshTitleBusy: 'Refreshing from Passgate…',
    refreshTitleIdle: 'Pull the latest app list from Passgate',
    refreshingAria: 'Refreshing software list from Passgate.',
    emptyTitle: 'No software registered in Passgate yet.',
    emptyHint: 'Add the product in Passgate, then click Refresh.',
    filter: {
      ariaText: 'Filter software',
      placeholderText: 'Filter by name, id, description, or repository…  (press / to focus)',
      channelAria: 'Filter by channel',
      channelAll: 'All channels',
      channelCount: '{count} channels',
      channelNone: 'No channels yet',
      activityAria: 'Filter by latest activity',
      clear: 'Clear filters',
      emptyMatch: 'No software matches the current filter.',
    },
    tree: {
      all: 'All apps',
      ungrouped: 'Ungrouped',
    },
    activity: {
      any: 'Any activity',
      recent7: 'Released in 7 days',
      recent30: 'Released in 30 days',
      stale90: 'No release in 90 days',
      never: 'Never released',
    },
    showingOne: 'Showing {filtered} of {total} software',
    showingOther: 'Showing {filtered} of {total} software',
    filteredSuffix: '(filtered)',
    export: 'Export CSV',
    exportTitleEmpty: 'Nothing to export — current filter has no matches.',
    exportTitle: 'Download {count} software as CSV (one column per channel pointer).',
    toastExportedOne: 'Exported {count} software as {filename}.',
    toastExportedOther: 'Exported {count} software as {filename}.',
    toastRefreshedOne: 'Software list refreshed; {count} item.',
    toastRefreshedOther: 'Software list refreshed; {count} items.',
    errorLoad: 'Failed to load software',
    errorRefreshWithMsg: 'Refresh failed: {msg}. Showing cached list.',
    errorRefresh: 'Refresh failed; showing cached list.',
    retiredBadge: 'Retired',
  },
  softwareDetail: {
    back: 'All software',
    promoteDisabledReason: 'Nothing to promote — register a release first.',
    quickLinks: {
      repository: 'Repository',
      passgateLabel: 'Passgate',
      passgateTitle: 'Passgate application id — governs publish ACLs for this software.',
    },
    lifecycle: {
      retiredBadge: 'Retired',
      retire: 'Retire',
      retiring: 'Retiring…',
      unretire: 'Unretire',
      unretiring: 'Unretiring…',
      retirePrompt: 'Reason for retiring (optional):',
      retireConfirm: 'Retire {name}? This rejects every write (register / promote / ACL) with 409 and pauses daily train ticks. Reads keep working and you can unretire later.',
      retireToast: 'Retired {name}.',
      unretireToast: 'Restored {name} to active.',
      error: 'Could not {verb}: {msg}',
      errorElevate: 'Retire / unretire is owner-only. Step up first: {cmd}',
      retiredBannerTitle: 'This software is retired.',
      retiredBannerBody: 'Every write is rejected (409) and daily ticks are paused; reads stay available.',
    },
    pointers: {
      title: 'Channel pointers',
      emptyBefore: 'No channel pointers yet. Use Register above (or',
      emptyAfter: ') to seed the first channel.',
      internalToggle: 'Internal channels ({count})',
    },
    calendar: {
      title: 'Channel calendar',
      subtitle: 'Devel → beta → stable activity over the last {days} days, derived from recent releases.',
      empty: 'No release activity in the last 42 days — the calendar will populate once a release is registered or promoted.',
    },
    history: {
      title: 'Recent releases',
      countSuffixOne: 'latest {count}, newest first',
      countSuffixOther: 'latest {count}, newest first',
      emptyBefore: 'No releases registered yet. Use Register above, or the CLI',
      emptyAfter: '.',
      colExpand: 'Details',
      expandAria: 'Toggle artifacts + audit for {version}',
      colVersion: 'Version',
      colChannel: 'Channel',
      colQuality: 'Quality',
      colCommit: 'Commit',
      colRegistered: 'Registered',
      colBy: 'By',
    },
  },
  releaseDetail: {
    artifacts: 'Artifacts',
    artifactsEmpty: 'No artifacts recorded for this release.',
    colKind: 'Kind',
    colFilename: 'Filename',
    colBackend: 'Backend',
    colSize: 'Size',
    colSha: 'sha256',
    colState: 'State',
    provenance: 'Provenance',
    builder: 'Builder',
    builtAt: 'Built',
    sbom: 'SBOM',
    attestation: 'Attestation',
    dependsOn: 'Depends on',
    anyVersion: 'any',
    audit: 'Audit trail',
    auditError: 'Could not load audit: {msg}',
    auditEmpty: 'No audit events.',
    evRegister: 'register',
    evPromote: 'promote',
    ciJob: 'CI job ↗',
  },
  channels: {
    title: 'Channels',
    subtitle: 'Three-channel release pipeline: Devel → Beta → Stable.',
    fallbackBanner: '{msg} Showing default pipeline.',
    level: 'Level {n}',
    deployed: 'Currently deployed',
    emptyForChannel: 'No versions promoted to {ch} yet.',
    csvButton: 'CSV',
    csvTitle: 'Download all {count} {ch} releases as CSV.',
    csvAria: 'Download {ch} channel as CSV',
    toastExportedOne: 'Exported {count} release from {ch} as {filename}.',
    toastExportedOther: 'Exported {count} releases from {ch} as {filename}.',
    fallback: {
      devel: 'Earliest builds — automatic from CI',
      beta: 'Promoted from Devel after extended testing',
      stable: 'Production-ready — promoted from Beta',
    },
  },
  trains: {
    resource: 'release trains',
    title: 'Release trains',
    subtitle: 'Company-level canary cadence: a daily-cron schedule auto-bumps Y for every active software each tick; New cycle rolls the shared major X.',
    refresh: 'Refresh',
    newTrain: 'New train',
    createTitle: 'Create release train',
    fieldName: 'Name',
    namePlaceholder: 'company',
    fieldAnchor: 'Anchor date',
    fieldCron: 'Daily cron',
    fieldTimezone: 'Timezone',
    fieldMajor: 'Current major (X)',
    cancel: 'Cancel',
    create: 'Create',
    creating: 'Creating…',
    empty: 'No release trains yet. Create one to drive the daily canary cadence.',
    cycleLabel: 'cycle {x}',
    disabledBadge: 'Disabled',
    anchor: 'anchor {date}',
    updated: 'updated {ago}',
    enable: 'Enable',
    disable: 'Disable',
    newCycle: 'New cycle',
    newCycleTitle: 'Roll to the next major (X+1) and reset active software to X.0.0.',
    newCycleConfirm: 'Open a new cycle on {name}? current_major → {next}. Active software starts the next canary on X.0.0.',
    tick: 'Tick now',
    tickTitle: 'Fire the daily canary bump immediately (Y++ for every active software).',
    tickDisabledTitle: 'Train is disabled — enable it to tick.',
    tickConfirm: 'Fire {name}’s daily tick now? Every active software gets a canary Y++.',
    cronLabel: 'Daily cron',
    timezoneLabel: 'Timezone',
    saveSchedule: 'Save',
    saving: 'Saving…',
    cronHint: 'Standard 5-field cron in the train’s timezone. Saving re-registers the scheduler at the next sync — no restart.',
    bindingsToggle: 'Software bindings',
    bindingsEmpty: 'No software bound to this train yet.',
    pickSoftware: 'Add software…',
    addBinding: 'Add',
    part: {
      active: 'active',
      frozen: 'frozen',
      archived: 'archived',
    },
    toastScheduleSaved: 'Saved schedule for {name}.',
    toastEnabled: 'Enabled {name}.',
    toastDisabled: 'Disabled {name}.',
    toastNewCycle: 'Opened cycle {major} on {name}.',
    toastTick: 'Ticked {name}: {bumped} bumped, {skipped} skipped.',
    toastTickNone: 'Ticked {name}: nothing to bump.',
    toastBinding: 'Set {name} → {p}.',
    toastCreated: 'Created train {name}.',
    errorAction: 'Action failed: {msg}',
    readOnlyTitle: 'Requires the platform sysadmin role.',
    readOnlyBanner: 'Read-only: train management requires the platform sysadmin role. You can view cadence and bindings, but creating, editing, ticking, or re-binding is disabled.',
  },
  calendar: {
    title: 'Channel Calendar',
    previewBanner: 'Preview: the calendar backend (/api/v1/calendar) is not yet implemented — what you see is mock data. The view will switch to live data once the endpoint ships.',
    subtitlePrefix: '3-channel × multi-release timeline view ·',
    subtitleDaysWord: 'days',
    subtitleStarting: 'starting',
    prevWeek: 'Previous week',
    today: 'Today',
    nextWeek: 'Next week',
    prevWeekLabel: '‹ 1w',
    todayLabel: 'Today',
    nextWeekLabel: '1w ›',
    windowAria: 'window size',
    weeks4: '4 weeks',
    weeks6: '6 weeks',
    weeks8: '8 weeks',
    weeks12: '12 weeks',
  },
  promote: {
    button: 'Promote…',
    dialogTitle: 'Promote release',
    dialogSubtitle: 'Move a release one step up the channel ladder. Only forward, one-hop transitions are allowed.',
    fromLabel: 'Source channel',
    toLabel: 'Target channel',
    toAuto: 'auto: {to}',
    toAtTop: 'No channel above this one — nothing to promote to.',
    internalNote: 'test / canary are internal channels, surfaced only during hot-patch / replay flows.',
    sourceHeading: 'Source release',
    sourceLoading: 'Loading source release…',
    sourceEmpty: 'No release currently on {channel} — nothing to promote.',
    sourceError: 'Could not load source release: {msg}',
    sourceVersion: 'Version',
    sourceCommit: 'Commit',
    sourceRegistered: 'Registered',
    sourceRegisteredBy: 'By',
    sourceQuality: 'Quality',
    qualityWarningBroken: 'Quality grade is {q}. Promoting is allowed but a broken release should rarely be moved forward — confirm with the release owner first.',
    qualityWarningUnreviewed: 'Quality grade is {q} — the calendar daemon has not yet rendered a verdict. You can still promote, but consider waiting for the green check.',
    versionLabel: 'Target version (X.Y.Z)',
    versionHint: 'Required when promoting test → canary: the canary release needs a real semver, not the source commit’s 7-hex sha.',
    versionWhyTestToCanary: 'Why a version is needed here: the test channel records the commit short SHA in the version field. Canary and above need a real X.Y.Z; once you set it, subsequent promotions inherit this value verbatim.',
    versionPlaceholder: '1.2.3',
    versionInvalid: 'Must be X.Y.Z (digits only, two dots).',
    reasonLabel: 'Reason (optional)',
    reasonPlaceholder: 'Short audit note — why are you promoting now?',
    submit: 'Promote',
    submitting: 'Promoting…',
    cancel: 'Cancel',
    toastSuccess: 'Promoted {software} from {from} to {to}.',
    error400: '{msg}',
    error403: 'You do not have permission to promote to {to}.',
    error404: 'No release found on {from} — nothing to promote.',
    error409: 'Conflict — the source channel pointer changed since you opened this dialog. Re-open and try again.',
    errorGeneric: 'Promote failed: {msg}',
    elevateTitle: 'Step up to the {role} role',
    elevateBody: 'This channel requires {required}; your session holds {current}. Use “Adjust role” (account menu) to switch your session and retry, or mint a one-off token for the CLI / API path:',
    elevateThen: 'The elevated token is valid ~1h; the backend grants it only if your passgate UserAppRole allows it.',
  },
  register: {
    button: 'Register release…',
    sectionNavAria: 'Form section navigation',
    requiredCount: '{n} required',
    pageTitle: 'Register release',
    pageSubtitle: 'Manually attach a multi-artifact release manifest to {software}. Normally driven by CI — use this for hot-patches, replays, or one-off corrections.',
    backToDetail: '← Back to software',
    section: {
      basics: 'Basics',
      artifacts: 'Artifacts',
      provenance: 'Provenance (optional)',
      dependsOn: 'Depends on (optional)',
      audit: 'Audit (optional)',
    },
    commitShaLabel: 'Commit SHA (40-hex)',
    commitShaPlaceholder: 'a1b2c3… (full 40 char lowercase hex)',
    commitShaInvalid: 'Must be exactly 40 lowercase hex characters.',
    versionLabel: 'Version',
    versionPlaceholderCanary: 'X.Y.Z (or leave empty for 0.0.0+g sentinel)',
    versionPlaceholderTest: '7-hex commit short sha (e.g. {short})',
    versionInvalidTest: 'On the test channel version must be the 7-hex commit short sha.',
    versionInvalidCanary: 'Version must be X.Y.Z (digits only) when set.',
    channelLabel: 'Channel',
    channelHintTest: 'test — push-time CI build for a test/* branch. Version = 7-hex short sha.',
    channelHintCanary: 'canary — versioned pre-release. Version typically reserved via /version/reserve.',
    artifactsHint: 'At least one artifact required. Each artifact must have kind, filename, location_type, and location_uri.',
    artifactKind: 'Kind',
    artifactFilename: 'Filename',
    artifactLocationType: 'Backend',
    artifactLocationUri: 'URI',
    artifactSha256: 'sha256 (optional)',
    artifactSizeBytes: 'size bytes (optional)',
    artifactContentType: 'content_type (optional)',
    artifactAdd: '+ Add artifact',
    artifactRemove: 'Remove',
    provenanceSbomRef: 'SBOM ref',
    provenanceAttestationRef: 'Attestation ref',
    provenanceBuiltAt: 'Built at',
    provenanceBuilder: 'Builder',
    provenanceSidecarRefs: 'Sidecar refs (one per line)',
    dependsOnRelease: 'Release',
    dependsOnVersion: 'Version (optional)',
    dependsOnChannel: 'Channel',
    dependsOnConstraint: 'Constraint (optional)',
    dependsOnAdd: '+ Add dependency',
    dependsOnRemove: 'Remove',
    auditRegisteredBy: 'registered_by (override)',
    auditRegisteredByHint: 'Leave empty to use your passgate identity.',
    auditBuilder: 'Builder',
    auditCiJobUrl: 'CI job URL',
    auditNote: 'Note',
    submit: 'Register',
    submitting: 'Registering…',
    cancel: 'Cancel',
    toastSuccess: 'Registered {software} v{version} on {channel}.',
    toastSuccessNoVersion: 'Registered {software} on {channel}.',
    error400: '{msg}',
    error403: 'You do not have permission to register releases for {software}.',
    error409: 'A release with this commit + channel already exists.',
    errorGeneric: 'Register failed: {msg}',
    needsArtifact: 'Add at least one artifact before submitting.',
  },
}
